import clsx from "clsx";
import * as React from "react";
import { IconName } from "@fortawesome/fontawesome-common-types";
import PillButton from "./PillButton";

export type SelectOption = {
  value: string;
  label: string | React.ReactNode;
  selected?: boolean;
  icon?: IconName;
  disabled?: boolean;
  /** Preferred typeahead haystack when `label` is a ReactNode. */
  searchText?: string;
};

type ComboBoxDisplayProps = {
  selectedOptions: SelectOption | SelectOption[];
  placeHolder: string;
  query: string;
  setQuery: Function;
  removeSelection: Function;
  isMulti?: boolean;
  error?: boolean;
  value?: SelectOption | SelectOption[];
  warning?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value">;

const hiddenInputClassName = clsx(
  "absolute",
  "inset-0",
  "h-full",
  "w-full",
  "cursor-pointer",
  "border-0",
  "bg-transparent",
  "p-0",
  "opacity-0"
);

const ComboBoxDisplay = React.forwardRef<HTMLInputElement, ComboBoxDisplayProps>(
  (
    {
      selectedOptions,
      placeHolder,
      query,
      setQuery,
      isMulti,
      value,
      removeSelection,
      error,
      warning,
      onKeyDown,
      onBlur,
      onClick,
      onChange: headlessOnChange,
      className: _headlessClassName,
      ...headlessInputProps
    },
    ref
  ) => {
    const classes = error
      ? "bg-[#db271b]/25 border-[#db271b]"
      : warning
      ? "bg-[#f1b825]/25 border-[#f1b825]"
      : "!bg-panel border-gray-300";

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      headlessOnChange?.(e);
    };

    const headlessInputElementProps = {
      ...headlessInputProps,
      ref,
      "data-combobox-input": true,
      onKeyDown,
      onBlur,
      onClick,
      onChange: handleInputChange,
      value: query,
    };

    if (isMulti) {
      const pillDisplays: React.ReactNode[] = [];
      let extraSelected = 0;
      ((value || selectedOptions) as SelectOption[]).forEach((option, index) => {
        if (index < 3) {
          pillDisplays.push(
            <PillButton
              key={`${option.value}-${index}`}
              text={option.label}
              removeSelection={() => removeSelection(option.value)}
            />
          );
        } else {
          extraSelected++;
        }
      });

      const showSearchInput = pillDisplays.length === 0 || query.length > 0;

      return (
        <div
          data-combobox-display
          className={clsx(
            "relative",
            "flex",
            "w-full",
            "overflow-hidden",
            "rounded-lg",
            "border",
            "py-2",
            "pl-3",
            "pr-5",
            "text-sm",
            "leading-5",
            "focus:ring-0",
            classes
          )}
        >
          {pillDisplays.length > 0 && (
            <div
              className={clsx(
                "flex",
                "flex-wrap",
                "w-full",
                "items-center",
                "rounded-lg",
                "gap-1"
              )}
            >
              {pillDisplays}
              {extraSelected > 0 && (
                <PillButton text={`+${extraSelected.toString()}`} />
              )}
            </div>
          )}
          {showSearchInput ? (
            <input
              {...headlessInputElementProps}
              className={clsx(
                "flex",
                "w-full",
                "rounded-lg",
                "bg-transparent",
                "px-3",
                "text-sm",
                "leading-5",
                "!outline-none"
              )}
              placeholder={placeHolder}
            />
          ) : (
            <input {...headlessInputElementProps} className={hiddenInputClassName} />
          )}
        </div>
      );
    }

    const displayValue = (value || selectedOptions) as SelectOption;
    const showLabel = !!displayValue.label;

    return (
      <div
        data-combobox-display
        className={clsx(
          "relative",
          "flex",
          "items-center",
          "w-full",
          "space-x-1",
          "rounded-lg",
          "border",
          "py-2",
          "pl-3",
          "pr-5",
          "text-sm",
          "leading-5",
          "focus:ring-0",
          classes
        )}
      >
        {showLabel && (
          <span
            className={clsx(
              "pointer-events-none",
              "flex-1",
              "min-w-0",
              typeof displayValue.label === "string" && "whitespace-nowrap"
            )}
          >
            {displayValue.label}
          </span>
        )}
        {showLabel ? (
          <input {...headlessInputElementProps} className={hiddenInputClassName} />
        ) : (
          <input
            {...headlessInputElementProps}
            className={clsx(
              "flex",
              "w-full",
              "rounded-lg",
              "bg-transparent",
              "px-0",
              "text-sm",
              "leading-5",
              "!outline-none",
              error
                ? "placeholder-[#db271b]"
                : warning
                ? "placeholder-[#f1b825]"
                : ""
            )}
            placeholder={placeHolder}
          />
        )}
      </div>
    );
  }
);

ComboBoxDisplay.displayName = "ComboBoxDisplay";

export default ComboBoxDisplay;
